import { createAsyncThunk } from '@reduxjs/toolkit';

import { ReportReadStatus, ReportSourceType } from '@/core/interfaces/common';
import { RootState } from '@/core/interfaces/store';
import { getAllAppliedFiltersSelector } from '@/core/store/reducers/filtersSlice';

import { api } from '@/features/Reports/api';
import {
  Report,
  ReportResponseItemWithReadStatus,
  ReportSource,
  ReportsListData,
} from '@/features/Reports/interfaces';
import { resolveReportSourcesMediaViaApi } from '@/features/Reports/media-resolution';

import { getActionPrefix } from '@/utils/helpers';
import { getReportsDatabaseResults } from '@/utils/getReportsDatabaseResults';
import { transformAppliedFiltersToPostDataForSourceRefresh } from '@/utils/filters';

import {
  appendHorizonReports,
  appendLookoutReports,
  appendMulticolumnHorizonReport,
  appendMulticolumnLookoutReport,
  updateReportSources,
} from './slices';

const actionPrefix = getActionPrefix('reports');

export const getHorizonReportsList = createAsyncThunk<
  Array<ReportResponseItemWithReadStatus>,
  ReportsListData
>(
  `${actionPrefix}/getHorizonReportsList`,
  async (filtersQueryParams, { signal, dispatch, getState }) => {
    const userId = (getState() as RootState).auth.user?.id;
    let pageNumber = 1;
    let shouldFetch = true;
    const reportsList: Array<ReportResponseItemWithReadStatus> = [];
    const shouldFetchAllPages = !filtersQueryParams.page;
    let reportsSavedInDatabase: Array<number> = [];

    if (userId) {
      const userReports = await getReportsDatabaseResults(userId);

      reportsSavedInDatabase = userReports?.horizonReportIds ?? [];
    }

    // We have to fetch all pages in one go as we don't have pagination views on the map
    do {
      try {
        const { data } = await api.getHorizonReportsList(
          {
            page: pageNumber,
            ...filtersQueryParams,
          },
          signal
        );

        const results = data.results.map<ReportResponseItemWithReadStatus>(item => ({
          ...item,
          readStatus: reportsSavedInDatabase.includes(item.reportId)
            ? ReportReadStatus.READ
            : ReportReadStatus.UNREAD,
        }));

        if (shouldFetchAllPages) {
          dispatch(appendHorizonReports(results));
        }

        reportsList.push(...results);

        pageNumber += 1;
        shouldFetch = !!data.next;
      } catch {
        shouldFetch = false;
      }
    } while (shouldFetch && shouldFetchAllPages);

    return reportsList;
  }
);

export const getLookoutReportsList = createAsyncThunk<
  Array<ReportResponseItemWithReadStatus>,
  ReportsListData
>(
  `${actionPrefix}/getLookoutReportsList`,
  async (filtersQueryParams, { signal, dispatch, getState }) => {
    const userId = (getState() as RootState).auth.user?.id;
    let pageNumber = 1;
    let shouldFetch = true;
    const reportsList: Array<ReportResponseItemWithReadStatus> = [];
    const shouldFetchAllPages = !filtersQueryParams.page;
    let reportsSavedInDatabase: Array<number> = [];

    if (userId) {
      const userReports = await getReportsDatabaseResults(userId);

      reportsSavedInDatabase = userReports?.lookoutReportIds ?? [];
    }

    // We have to fetch all pages in one go as we don't have pagination views on the map
    do {
      try {
        const { data } = await api.getLookoutReportsList(
          {
            page: pageNumber,
            ...filtersQueryParams,
          },
          signal
        );

        const results = data.results.map<ReportResponseItemWithReadStatus>(item => ({
          ...item,
          readStatus: reportsSavedInDatabase.includes(item.reportId)
            ? ReportReadStatus.READ
            : ReportReadStatus.UNREAD,
        }));

        if (shouldFetchAllPages) {
          dispatch(appendLookoutReports(results));
        }

        reportsList.push(...results);

        pageNumber += 1;
        shouldFetch = !!data.next;
      } catch {
        shouldFetch = false;
      }
    } while (shouldFetch && shouldFetchAllPages);

    return reportsList;
  }
);

export const getHorizonReportsListForMulticolumn = createAsyncThunk<
  Array<ReportResponseItemWithReadStatus>,
  {
    focusId: number;
    filtersQueryParams: ReportsListData;
  }
>(
  `${actionPrefix}/getHorizonReportsListForMulticolumn`,
  async ({ focusId, filtersQueryParams }, { signal, dispatch, getState }) => {
    const userId = (getState() as RootState).auth.user?.id;
    let pageNumber = 1;
    let shouldFetch = true;
    const reportsList: Array<Report> = [];
    const shouldFetchAllPages = !filtersQueryParams.page;
    let reportsSavedInDatabase: Array<number> = [];

    if (userId) {
      const userReports = await getReportsDatabaseResults(userId);

      reportsSavedInDatabase = userReports?.horizonReportIds ?? [];
    }

    // We have to fetch all pages in one go as we don't have pagination views on the map
    do {
      try {
        const { data } = await api.getHorizonReportsList(
          {
            page: pageNumber,
            ...filtersQueryParams,
          },
          signal
        );

        const reportsResults = data.results.map<Report>(item => ({
          ...item,
          wasOpened: false,
          reportSourceType: ReportSourceType.HORIZON,
          readStatus: reportsSavedInDatabase.includes(item.reportId)
            ? ReportReadStatus.READ
            : ReportReadStatus.UNREAD,
        }));

        if (shouldFetchAllPages) {
          dispatch(
            appendMulticolumnHorizonReport({
              focusId,
              reports: reportsResults,
            })
          );
        } else {
          reportsList.push(...reportsResults);
        }

        pageNumber += 1;
        shouldFetch = !!data.next;
      } catch {
        shouldFetch = false;
      }
    } while (shouldFetch && shouldFetchAllPages);

    return reportsList;
  }
);

export const getLookoutReportsListForMulticolumn = createAsyncThunk<
  Array<ReportResponseItemWithReadStatus>,
  {
    focusId: number;
    filtersQueryParams: ReportsListData;
  }
>(
  `${actionPrefix}/getLookoutReportsListForMulticolumn`,
  async ({ focusId, filtersQueryParams }, { signal, dispatch, getState }) => {
    const userId = (getState() as RootState).auth.user?.id;
    let pageNumber = 1;
    let shouldFetch = true;
    const reportsList: Array<Report> = [];
    const shouldFetchAllPages = !filtersQueryParams.page;
    let reportsSavedInDatabase: Array<number> = [];

    if (userId) {
      const userReports = await getReportsDatabaseResults(userId);

      reportsSavedInDatabase = userReports?.lookoutReportIds ?? [];
    }

    // We have to fetch all pages in one go as we don't have pagination views on the map
    do {
      try {
        const { data } = await api.getLookoutReportsList(
          {
            page: pageNumber,
            ...filtersQueryParams,
          },
          signal
        );

        const reportsResults = data.results.map<Report>(item => ({
          ...item,
          wasOpened: false,
          reportSourceType: ReportSourceType.LOOKOUT,
          readStatus: reportsSavedInDatabase.includes(item.reportId)
            ? ReportReadStatus.READ
            : ReportReadStatus.UNREAD,
        }));

        if (shouldFetchAllPages) {
          dispatch(
            appendMulticolumnLookoutReport({
              focusId,
              reports: reportsResults,
            })
          );
        } else {
          reportsList.push(...reportsResults);
        }

        pageNumber += 1;
        shouldFetch = !!data.next;
      } catch {
        shouldFetch = false;
      }
    } while (shouldFetch && shouldFetchAllPages);

    return reportsList;
  }
);

export const refreshReportSources = createAsyncThunk<
  Array<ReportSource>,
  {
    reportId: number;
    reportSourceType: ReportSourceType;
  },
  { rejectValue: null }
>(
  `${actionPrefix}/refreshReportSources`,
  async ({ reportId, reportSourceType }, { signal, dispatch, getState, rejectWithValue }) => {
    const appliedFilters = getAllAppliedFiltersSelector(getState() as RootState);
    const filtersQueryParams = transformAppliedFiltersToPostDataForSourceRefresh(appliedFilters);

    let pageNumber = 1;
    let shouldFetch = true;

    while (shouldFetch) {
      try {
        const { data } =
          reportSourceType === ReportSourceType.HORIZON
            ? await api.getHorizonReportsList(
                {
                  page: pageNumber,
                  ...filtersQueryParams,
                },
                signal
              )
            : await api.getLookoutReportsList(
                {
                  page: pageNumber,
                  ...filtersQueryParams,
                },
                signal
              );

        const report = data.results.find(item => item.reportId === reportId);

        if (report) {
          const resolvedSources = await resolveReportSourcesMediaViaApi(report.source);

          dispatch(
            updateReportSources({
              reportId,
              reportSourceType,
              sources: resolvedSources,
            })
          );

          return resolvedSources;
        }

        pageNumber += 1;
        shouldFetch = !!data.next;
      } catch {
        return rejectWithValue(null);
      }
    }

    return rejectWithValue(null);
  }
);
